/**
 * FR-090…094 · Quản lý quyền tên miền — bám bố cục `domain-roles.html`:
 * danh sách mức quyền · chi tiết + 16 capability 3 nhóm · ma trận so sánh.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, thanhApiError, useLoiApi } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { roleBadge } from '../../lib/format';
import type { CapabilityGroup, ComparisonMatrix, DomainRole } from '../../lib/types';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { Field, SelectField } from '../../components/Field';
import { CapabilityMatrix } from '../../components/CapabilityMatrix';
import { ComparisonTable } from '../../components/ComparisonTable';
import { AsyncSection, EmptyState, SkeletonCard } from '../../components/States';
import { useToast } from '../../components/Toast';

export function DomainRolesPage() {
  const t = useT();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const rolesQuery = useQuery({
    queryKey: ['domain-roles'],
    queryFn: () => api.get<DomainRole[]>('/domain-roles'),
  });
  const capsQuery = useQuery({
    queryKey: ['domain-roles', 'capabilities'],
    queryFn: () => api.get<CapabilityGroup[]>('/domain-roles/capabilities'),
    staleTime: 10 * 60 * 1000,
  });
  const matrixQuery = useQuery({
    queryKey: ['domain-roles', 'matrix'],
    queryFn: () => api.get<ComparisonMatrix>('/domain-roles/matrix'),
  });

  const roles = rolesQuery.data ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [editMeta, setEditMeta] = useState(false);

  const selected = useMemo(
    () => roles.find((r) => r.id === selectedId) ?? roles[0] ?? null,
    [roles, selectedId],
  );

  useEffect(() => {
    if (selected) {
      setDraft(selected.capabilities);
      setDirty(false);
    }
  }, [selected?.id, selected?.capabilities.join(',')]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['domain-roles'] });
  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  const saveCaps = useMutation({
    mutationFn: (payload: { id: string; capabilities: string[] }) =>
      api.patch<DomainRole>(`/domain-roles/${payload.id}`, { capabilities: payload.capabilities }),
    onSuccess: async (role) => {
      await invalidate();
      setDirty(false);
      toast(
        role.affectedGrants && role.affectedGrants > 0
          ? t('domainRoles.affected', { count: role.affectedGrants })
          : t('domainRoles.updated', { name: role.name }),
      );
    },
    onError,
  });

  const duplicate = useMutation({
    mutationFn: (id: string) => api.post<DomainRole>(`/domain-roles/${id}/duplicate`),
    onSuccess: async (role) => {
      await invalidate();
      setSelectedId(role.id);
      toast(t('roles.duplicated', { name: role.name }));
    },
    onError,
  });

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 data-od-id="dr-title">{t('domainRoles.title')}</h1>
          <p className="lede">{t('domainRoles.lede')}</p>
        </div>
        <div className="row">
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => setShowCreate(true)}
            data-od-id="dr-create"
          >
            <Icon name="plus" size={16} />
            {t('domainRoles.add')}
          </button>
        </div>
      </div>

      <AsyncSection
        loading={rolesQuery.isLoading || capsQuery.isLoading}
        error={rolesQuery.error ?? capsQuery.error}
        data={roles.length > 0 && capsQuery.data ? roles : undefined}
        onRetry={() => void rolesQuery.refetch()}
        skeleton={<SkeletonCard rows={6} />}
        empty={<EmptyState title={t('state.empty.title')} />}
      >
        {() => (
          <>
            <div className="split-roles">
              <div className="card" data-od-id="dr-list-card">
                <div className="card-head" style={{ padding: '10px 12px' }}>
                  <span className="eyebrow grow">{t('domainRoles.listTitle')}</span>
                  <span className="small muted">
                    {t('domainRoles.count', { count: roles.length })}
                  </span>
                </div>
                <div className="card-body" style={{ padding: 8 }}>
                  <div className="picker" data-od-id="dr-picker">
                    {roles.map((role) => (
                      <button
                        key={role.id}
                        type="button"
                        aria-pressed={selected?.id === role.id}
                        onClick={() => setSelectedId(role.id)}
                      >
                        <span className="badge-lvl">{roleBadge(role.slug)}</span>
                        <span className="who">
                          <b>{role.name}</b>
                          <span>
                            {role.isSystem ? `${t('common.system')} · ` : ''}
                            {role.isDefault ? `${t('common.default')} · ` : ''}
                            {t('domainRoles.grantCount', { count: role.grantCount })}
                          </span>
                          <span
                            className="bar"
                            style={{ marginTop: 5 }}
                            aria-label={`${t('domainRoles.level')} ${role.level}%`}
                          >
                            <i style={{ width: `${role.level}%` }} />
                          </span>
                        </span>
                        <span className="n">{role.level}%</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="card-foot">
                  <button
                    className="btn btn-sm btn-block"
                    type="button"
                    onClick={() => setShowCreate(true)}
                  >
                    {t('domainRoles.add')}
                  </button>
                </div>
              </div>

              {selected ? (
                <div className="card" data-od-id="dr-detail">
                  <div className="card-head">
                    <span className="badge-lvl">{roleBadge(selected.slug)}</span>
                    <div className="grow">
                      <h2>{selected.name}</h2>
                      <div className="sub">{selected.description ?? '—'}</div>
                    </div>
                    {selected.isSystem ? (
                      <span className="chip">
                        <span className="dot" />
                        {t('common.system')}
                      </span>
                    ) : null}
                    {selected.isDefault ? (
                      <span className="chip accent">
                        <span className="dot" />
                        {t('common.default')}
                      </span>
                    ) : null}
                    <div className="row">
                      <button
                        className="btn btn-sm"
                        type="button"
                        disabled={selected.isSystem}
                        onClick={() => setEditMeta(true)}
                        data-od-id="dr-edit"
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        className="btn btn-sm"
                        type="button"
                        onClick={() => duplicate.mutate(selected.id)}
                        disabled={duplicate.isPending}
                      >
                        {t('common.duplicate')}
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        type="button"
                        disabled={selected.isSystem || selected.isDefault}
                        onClick={() => setShowDelete(true)}
                        data-od-id="dr-delete"
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>

                  <div className="card-body" style={{ paddingTop: 12, paddingBottom: 12 }}>
                    {selected.isSystem ? (
                      <div className="scope-note">
                        <Icon name="info" size={17} />
                        <span>{t('domainRoles.systemNote')}</span>
                      </div>
                    ) : null}
                    {!selected.isSystem && selected.grantCount > 0 && dirty ? (
                      <div className="scope-note warn">
                        <Icon name="warn" size={17} />
                        <span>{t('domainRoles.affected', { count: selected.grantCount })}</span>
                      </div>
                    ) : null}
                  </div>

                  <div className="card-body" style={{ paddingTop: 0 }}>
                    <CapabilityMatrix
                      groups={capsQuery.data ?? []}
                      selected={draft}
                      readOnly={selected.isSystem}
                      lockedNote={t('domainRoles.systemNote')}
                      onToggle={(id, next) => {
                        setDraft((prev) =>
                          next ? [...prev, id] : prev.filter((cap) => cap !== id),
                        );
                        setDirty(true);
                      }}
                    />
                  </div>

                  <div className="card-foot">
                    <span className="small muted">{t('roles.footNote')}</span>
                    <span style={{ flex: 1 }} />
                    <button
                      className="btn btn-sm btn-primary"
                      type="button"
                      disabled={selected.isSystem || !dirty || saveCaps.isPending}
                      onClick={() => saveCaps.mutate({ id: selected.id, capabilities: draft })}
                    >
                      {saveCaps.isPending ? t('common.processing') : t('common.save')}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            {matrixQuery.data ? (
              <ComparisonTable
                matrix={matrixQuery.data}
                title={t('domainRoles.matrixTitle')}
                subtitle={t('domainRoles.matrixSub')}
                odId="dr-matrix"
              />
            ) : null}
          </>
        )}
      </AsyncSection>

      {showCreate ? (
        <DomainRoleFormModal
          groups={capsQuery.data ?? []}
          onClose={() => setShowCreate(false)}
          onDone={async (role) => {
            await invalidate();
            setSelectedId(role.id);
            setShowCreate(false);
            toast(t('domainRoles.created', { name: role.name }));
          }}
        />
      ) : null}

      {editMeta && selected ? (
        <DomainRoleFormModal
          groups={capsQuery.data ?? []}
          role={selected}
          onClose={() => setEditMeta(false)}
          onDone={async (role) => {
            await invalidate();
            setEditMeta(false);
            toast(t('domainRoles.updated', { name: role.name }));
          }}
        />
      ) : null}

      {showDelete && selected ? (
        <DeleteDomainRoleModal
          role={selected}
          roles={roles}
          onClose={() => setShowDelete(false)}
          onDone={async () => {
            await invalidate();
            setShowDelete(false);
            setSelectedId(null);
            toast(t('domainRoles.deleted'));
          }}
        />
      ) : null}
    </>
  );
}

function DomainRoleFormModal({
  groups,
  role,
  onClose,
  onDone,
}: {
  groups: CapabilityGroup[];
  role?: DomainRole;
  onClose: () => void;
  onDone: (role: DomainRole) => void | Promise<void>;
}) {
  const t = useT();
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [isDefault, setIsDefault] = useState(role?.isDefault ?? false);
  const [caps, setCaps] = useState<string[]>(role?.capabilities ?? []);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const mutation = useMutation({
    mutationFn: () =>
      role
        ? api.patch<DomainRole>(`/domain-roles/${role.id}`, { name, description, isDefault })
        : api.post<DomainRole>('/domain-roles', {
            name,
            description: description || null,
            capabilities: caps,
            isDefault,
          }),
    onSuccess: (data) => void onDone(data),
    onError: (err) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      wide={!role}
      title={role ? t('common.edit') : t('domainRoles.add')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.processing') : t('common.save')}
          </button>
        </>
      }
    >
      {error && error.fields.length === 0 ? (
        <div className="form-alert bad" role="alert">
          <Icon name="warn" size={16} />
          <span>{error.message}</span>
        </div>
      ) : null}

      <Field
        label={t('roles.name')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        minLength={2}
        autoFocus
        error={error?.fieldMessage('name')}
      />
      <Field
        label={t('roles.description')}
        value={description ?? ''}
        onChange={(e) => setDescription(e.target.value)}
      />
      <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
        <input
          className="check"
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
        <span className="small">{t('roles.setDefault')}</span>
      </label>

      {!role ? (
        <div>
          <span className="eyebrow">{t('roles.capabilities')}</span>
          {error?.fieldMessage('capabilities') ? (
            <div className="form-alert bad" role="alert" style={{ marginTop: 8 }}>
              <Icon name="warn" size={16} />
              <span>{error.fieldMessage('capabilities')}</span>
            </div>
          ) : null}
          <CapabilityMatrix
            groups={groups}
            selected={caps}
            onToggle={(id, next) =>
              setCaps((prev) => (next ? [...prev, id] : prev.filter((c) => c !== id)))
            }
          />
        </div>
      ) : null}
    </Modal>
  );
}

function DeleteDomainRoleModal({
  role,
  roles,
  onClose,
  onDone,
}: {
  role: DomainRole;
  roles: DomainRole[];
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const t = useT();
  const others = roles.filter((r) => r.id !== role.id);
  const [fallback, setFallback] = useState(others[0]?.id ?? '');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const mutation = useMutation({
    mutationFn: () =>
      api.del(
        `/domain-roles/${role.id}`,
        role.grantCount > 0 ? { fallbackDomainRoleId: fallback } : {},
      ),
    onSuccess: () => void onDone(),
    onError: (err) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('domainRoles.deleteTitle')}
      subtitle={t('roles.deleteBody', { name: role.name })}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-danger" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.processing') : t('common.delete')}
          </button>
        </>
      }
    >
      {error ? (
        <div className="form-alert bad" role="alert">
          <Icon name="warn" size={16} />
          <span>{error.message}</span>
        </div>
      ) : null}

      {role.grantCount > 0 ? (
        <>
          <div className="form-alert warn">
            <Icon name="info" size={16} />
            <span>{t('domainRoles.deleteInUse', { count: role.grantCount })}</span>
          </div>
          <SelectField
            label={t('domainRoles.fallbackRole')}
            value={fallback}
            onChange={(e) => setFallback(e.target.value)}
            required
          >
            {others.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </SelectField>
        </>
      ) : null}
    </Modal>
  );
}
